import axios, { AxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { EXPECTED_FLOW_SHORT_MESSAGES } from "./shortMessages";
import { recordCheckoutError, recordCheckoutSuccess } from "./checkoutTelemetry";
import { recordCheckoutEvent } from "@/utils/openreplay/checkoutEvents";

export const baseURL = import.meta.env.VITE_API_URL;

const api = axios.create({
  baseURL,
  timeout: 30000,
});

// Timeout maior para chamadas que dependem de integracao TIM lenta em
// tempo real (ex.: /core/elegibilidade — P99 ~13,5s, P99.9 ~60s no Kibana).
// Backend permite ate 30s no Guzzle PMID/WSO2 — front precisa de margem
// pra receber a resposta de erro estruturada antes de timeoutar.
export const TIM_LONG_REQUEST_TIMEOUT = 35_000;

// Endpoints de tracking/observabilidade que disparam em alto volume e nao
// bloqueiam o fluxo de venda. Falhas neles nao devem poluir o Sentry.
const SENTRY_IGNORED_PATHS = [/\/core\/jornada-usuario(\/|$|\?)/];

// Trunca request/response data antes de enviar para o Sentry. Eventos
// excessivamente grandes podem ser truncados ou rejeitados pelo relay,
// derrubando o evento todo. 10k chars cabem com folga e ainda preservam
// contexto util para debug.
const MAX_PAYLOAD_CHARS = 10_000;

function shouldReportToSentry(url: string | undefined): boolean {
  if (!url) return true;
  return !SENTRY_IGNORED_PATHS.some((pattern) => pattern.test(url));
}

function truncate(value: unknown): unknown {
  if (value == null) return value;
  if (typeof value === "string") {
    return value.length > MAX_PAYLOAD_CHARS
      ? value.slice(0, MAX_PAYLOAD_CHARS) + "...[truncated]"
      : value;
  }
  try {
    const serialized = JSON.stringify(value);
    if (serialized.length <= MAX_PAYLOAD_CHARS) return value;
    return serialized.slice(0, MAX_PAYLOAD_CHARS) + "...[truncated]";
  } catch (e) {
    // Cycle, BigInt, getter que lanca, etc. Preservar a causa para
    // facilitar debug em vez de perder em uma string opaca.
    return `[unserializable: ${e instanceof Error ? e.message : String(e)}]`;
  }
}

function isExpectedFlowResponse(error: AxiosError): boolean {
  const data = error.response?.data;
  if (!data || typeof data !== "object") return false;
  const shortMessage = (data as { shortMessage?: unknown }).shortMessage;
  if (typeof shortMessage !== "string") return false;
  return (EXPECTED_FLOW_SHORT_MESSAGES as ReadonlySet<string>).has(
    shortMessage
  );
}

api.interceptors.response.use(
  (response) => {
    recordCheckoutSuccess(response, recordCheckoutEvent);
    return response;
  },
  (error: AxiosError) => {
    // Cancelamentos do app (AbortController) nao sao erros — early return
    // antes de qualquer captura.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    recordCheckoutError(error, recordCheckoutEvent);

    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase();
    const isTimeout = error.code === "ECONNABORTED";

    // Try/catch defensivo: se Sentry.captureException lancar, queremos
    // continuar rejeitando com o AxiosError original — nunca trocar o erro
    // que o app esta esperando por um erro do SDK.
    try {
      if (
        shouldReportToSentry(url) &&
        !isExpectedFlowResponse(error)
      ) {
        Sentry.captureException(error, {
          tags: {
            "api.endpoint": url ?? "unknown",
            "api.method": method ?? "unknown",
            "api.status": status
              ? String(status)
              : isTimeout
                ? "timeout"
                : "network_error",
            ...(isTimeout ? { "api.timeout": "true" } : {}),
          },
          extra: {
            requestUrl: url,
            requestMethod: method,
            requestData: truncate(error.config?.data),
            requestParams: error.config?.params,
            responseStatus: status,
            responseData: truncate(error.response?.data),
            message: error.message,
            code: error.code,
          },
        });
      }
    } catch (sentryErr) {
      console.warn("[Sentry] falha ao reportar:", sentryErr);
    }

    return Promise.reject(error);
  }
);

type FakeStatus = "resolved" | "rejected";

export const fakeFetch = <T>(
  returnValue: T,
  status: FakeStatus
): Promise<T> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      switch (status) {
        case "resolved":
          return resolve(returnValue);
        default:
          return reject(returnValue);
      }
    }, 1000);
  });
};

export default api;
